import { useCallback, useState } from 'react';
import type {
  EmailBlock,
  EmailBlockData,
  EmailBlockType,
} from '@/api/email-builder/email-builder.types.ts';
import { BLOCK_TYPE_META } from '../email-builder.constants';

let blockCounter = 0;

/** Generates a process-unique block id (used as React key + for reordering). */
export function newBlockId(): string {
  blockCounter += 1;
  return `blk_${Date.now().toString(36)}_${blockCounter.toString(36)}`;
}

/** Creates a fresh block of the given type with default data. */
export function createBlock(type: EmailBlockType): EmailBlock {
  return {
    id: newBlockId(),
    type,
    data: BLOCK_TYPE_META[type].createDefault(),
  };
}

export interface UseEmailBlocks {
  blocks: EmailBlock[];
  addBlock: (type: EmailBlockType, index?: number) => void;
  updateBlock: (id: string, data: EmailBlockData) => void;
  removeBlock: (id: string) => void;
  moveBlock: (id: string, direction: -1 | 1) => void;
  /** Replaces the whole block list — used to apply AI assistant edits. */
  replaceBlocks: (blocks: EmailBlock[]) => void;
}

/** Ordered-block-list state for the email builder editor. */
export function useEmailBlocks(initial: EmailBlock[] = []): UseEmailBlocks {
  const [blocks, setBlocks] = useState<EmailBlock[]>(initial);

  const addBlock = useCallback((type: EmailBlockType, index?: number) => {
    setBlocks((prev) => {
      const next = [...prev];
      next.splice(index ?? next.length, 0, createBlock(type));
      return next;
    });
  }, []);

  const updateBlock = useCallback((id: string, data: EmailBlockData) => {
    setBlocks((prev) =>
      prev.map((block) => (block.id === id ? { ...block, data } : block)),
    );
  }, []);

  const removeBlock = useCallback((id: string) => {
    setBlocks((prev) => prev.filter((block) => block.id !== id));
  }, []);

  const moveBlock = useCallback((id: string, direction: -1 | 1) => {
    setBlocks((prev) => {
      const from = prev.findIndex((block) => block.id === id);
      const to = from + direction;
      if (from < 0 || to < 0 || to >= prev.length) return prev;
      const next = [...prev];
      [next[from], next[to]] = [next[to], next[from]];
      return next;
    });
  }, []);

  const replaceBlocks = useCallback((next: EmailBlock[]) => {
    setBlocks(next);
  }, []);

  return {
    blocks,
    addBlock,
    updateBlock,
    removeBlock,
    moveBlock,
    replaceBlocks,
  };
}
